import { nextTick, watch } from "vue";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";
import { useGsap } from "@sazka/utils/gsap-register";
import { debounce } from "@sazka/utils/debounce";
import { useStackedModals } from "@sazka/utils/composables/use-stacked-modals";
import { useSmartBannerPosition } from "@/shared/components/master-page/use-smartbanner-position";
import { useNgSession } from "@sazka/ng/ng-session";

export const useBottomSheet = (
    target: gsap.DOMTarget,
    options = { keepOpenTolerance: 20, speedToForceClose: 1500, headerHeight: 64, safeBottomHeight: 400 }
) => {
    const { gsap } = useGsap(Draggable, InertiaPlugin);
    const { addToStackedModals, removeFromStackedModals } = useStackedModals(); // treat this as modal
    const { notificationHeight } = useSmartBannerPosition();

    let onSheetClosed: () => void;

    // destroys the bottom sheet
    const destroy = () => {
        console.log("destroying");

        removeFromStackedModals();
        const el = Draggable.get(target);
        if (!el) return;
        el.kill();
    };

    // plays the animation and destroys the bottom sheet
    const hideAndDestroy = async () => {
        const el = Draggable.get(target);
        if (!el) return;
        await gsap.to(el.target, {
            duration: 0.3,
            y: document.body.clientHeight,
        });
        destroy();
    };

    const bottomPadding = 20;
    // gets the bounds for the bottom sheet
    // allowing it to be dragged up, if overflowed
    const getBounds = (elHeight: number) => {
        return {
            maxY: document.body.clientHeight - 200,
            minY: Math.min(
                document.body.clientHeight -
                    options.headerHeight -
                    notificationHeight -
                    elHeight -
                    bottomPadding +
                    options.safeBottomHeight,
                0
            ), // this goes into negatives, to allow the sheet to be dragged up
        };
    };

    const applyBounds = () => {
        const el = Draggable.get(target);
        if (!el) return;
        el.applyBounds(getBounds(el.target.clientHeight));
    };

    // creates the bottom sheet
    const create = (elH: number) => {
        console.log("creating");

        addToStackedModals();
        Draggable.create(target, {
            type: "y",
            bounds: getBounds(elH),
            edgeResistance: 0.95,
            dragResistance: 0,
            inertia: true,
            zIndexBoost: false, // prevents incrementing z-index on drag

            onDragEnd: () => {
                const el = Draggable.get(target);
                const velocity = InertiaPlugin.getVelocity(el.target, "y");

                if (el.y > options.keepOpenTolerance || velocity > options.speedToForceClose) {
                    onSheetClosed();
                }
            },

            onThrowComplete: () => {
                const el = Draggable.get(target);
                if (el.y <= 0) return;
                nextTick(() => {
                    gsap.to(el.target, {
                        duration: 0.3,
                        y: 0,
                    });
                });
            },
        });
        gsap.fromTo(target, { y: document.body.clientHeight }, { duration: 0.3, y: 0 });
    };

    window.addEventListener("resize", debounce(applyBounds, 100));

    const { isLoggedIn } = useNgSession();
    watch(isLoggedIn, () => {
        applyBounds();
    });

    return {
        create,
        destroy,
        applyBounds,
        onSheetClosed: (callback: () => void) => {
            onSheetClosed = callback;
        },
        hideAndDestroy,
    };
};
